/**
 * AdminWikiReviewQueue — fast triage for held (fuzzy-match) Wikimedia ingests.
 * Side-by-side: candidate image vs the Archive entity record, with license,
 * attribution, match confidence and duplicate flags. One-click approve/reject
 * per card + bulk approval. Approved assets publish instantly and propagate
 * to every surface via the shared media system.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Check, X, Loader2, ShieldCheck, AlertTriangle, ExternalLink, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { wikiReviewQueue, wikiReviewDecide } from "../../lib/api";
import { AuthImg } from "../media/AuthImg";
import RapidTriage from "./RapidTriage";

const confColor = (n) => (n >= 70 ? "#3FB950" : n >= 40 ? "#E0A526" : "#D92D20");

const QueueCard = ({ item, checked, onCheck, onDecide, busy, token, userToken }) => {
  const e = item.entity || {};
  const wikiUrl = (item.source || "").split("— ").pop();
  return (
    <div className="border border-[#2D2A26] bg-[#0d0c0a]"
         data-testid={`wiki-review-card-${item.id}`}>
      <div className="flex flex-col sm:flex-row">
        {/* Candidate image */}
        <div className="sm:w-[220px] shrink-0 bg-black border-b sm:border-b-0 sm:border-r border-[#2D2A26] flex items-center justify-center p-2">
          <AuthImg token={token} userToken={userToken} assetId={item.id}
                   alt={item.caption || e.name}
                   className="max-h-[190px] w-auto object-contain"
                   style={{ filter: "sepia(0.12)" }} />
        </div>
        {/* Entity record + provenance */}
        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-start gap-2">
            <input type="checkbox" checked={checked}
                   onChange={() => onCheck(item.id)}
                   data-testid={`wiki-review-check-${item.id}`}
                   className="mt-1 accent-[#D92D20]" />
            <div className="min-w-0">
              <Link to={e.url || "#"} target="_blank"
                    className="text-[15px] text-[#E4DFD3] font-heading hover:text-[#D92D20] inline-flex items-center gap-1.5">
                {e.name} <ExternalLink size={11} className="text-[#454038]" />
              </Link>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] mt-0.5">
                {e.kind || "entity"} · {e.cameo_count || 0} dossier appearance{(e.cameo_count || 0) !== 1 ? "s" : ""}
              </div>
            </div>
            {/* Confidence */}
            <div className="ml-auto text-right shrink-0" data-testid={`wiki-review-confidence-${item.id}`}>
              <div className="font-display text-xl leading-none"
                   style={{ color: confColor(item.confidence) }}>
                {item.confidence}
              </div>
              <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-[#757065]">match</div>
            </div>
          </div>

          <div className="mt-2 text-[12px] text-[#A39E93] leading-snug line-clamp-2">
            {item.caption}
          </div>

          {/* License + attribution */}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 border border-[#3FB950]/40 text-[#5DD4B3] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em]">
              <ShieldCheck size={9} /> {item.rights || "license unknown"}
            </span>
            {item.credit && (
              <span className="font-mono text-[9px] text-[#757065] truncate max-w-[260px]">
                © {item.credit}
              </span>
            )}
            {wikiUrl && wikiUrl.startsWith("http") && (
              <a href={wikiUrl} target="_blank" rel="noreferrer"
                 className="font-mono text-[9px] text-[#9FA8D4] hover:underline">
                source article ↗
              </a>
            )}
          </div>

          {/* Identity verification evidence (deterministic pipeline v2) */}
          {item.identity_check && (
            <div className="mt-2" data-testid={`wiki-review-identity-${item.id}`}>
              <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] ${
                item.identity_check.verdict === false
                  ? "border-[#D92D20]/60 text-[#FFA9A0]"
                  : "border-[#3FB950]/40 text-[#5DD4B3]"}`}>
                {item.identity_check.verdict === false ? <AlertTriangle size={9} /> : <ShieldCheck size={9} />}
                {item.identity_check.verdict === false ? "IDENTITY MISMATCH" : "identity verified"}
              </span>
              <span className="block mt-1 font-mono text-[9px] text-[#757065] leading-snug">
                {item.identity_check.reason}
                {item.identity_check.homonym_risk ? " · same-name risk — confirm subject" : ""}
              </span>
            </div>
          )}

          {/* Duplicate / already-covered flags */}
          {(item.flags?.target_already_published || item.flags?.hash_duplicates > 0) && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid={`wiki-review-flags-${item.id}`}>
              {item.flags.target_already_published && (
                <span className="inline-flex items-center gap-1 border border-[#E0A526]/50 text-[#E0A526] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em]">
                  <AlertTriangle size={9} /> entity already has published media
                </span>
              )}
              {item.flags.hash_duplicates > 0 && (
                <span className="inline-flex items-center gap-1 border border-[#E0A526]/50 text-[#E0A526] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em]">
                  <Layers size={9} /> {item.flags.hash_duplicates} identical file{item.flags.hash_duplicates !== 1 ? "s" : ""} in library
                </span>
              )}
            </div>
          )}

          {/* Decisions */}
          <div className="mt-3 flex gap-2">
            <button onClick={() => onDecide([item.id], "approve")} disabled={!!busy}
                    data-testid={`wiki-review-approve-${item.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#1d3b2a] border border-[#3FB950]/50 text-[#5DD4B3] hover:bg-[#255033] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50">
              <Check size={11} /> Approve
            </button>
            <button onClick={() => onDecide([item.id], "reject")} disabled={!!busy}
                    data-testid={`wiki-review-reject-${item.id}`}
                    className="inline-flex items-center gap-1.5 border border-[#D92D20]/50 text-[#FFA9A0] hover:bg-[#D92D20]/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50">
              <X size={11} /> Reject
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminWikiReviewQueue = ({ token, userToken }) => {
  const [queue, setQueue] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState("");
  const [visible, setVisible] = useState(12);
  const [triage, setTriage] = useState(false);
  const [open, setOpen] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("panel") === "wiki-review"; }
    catch { return false; }
  });

  const load = useCallback(async () => {
    try { setQueue((await wikiReviewQueue(token, userToken)).queue || []); }
    catch { setQueue([]); }
  }, [token, userToken]);

  useEffect(() => { load(); }, [load]);

  const onCheck = (id) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const onDecide = async (ids, action) => {
    setBusy(action);
    try {
      const r = await wikiReviewDecide(token, ids, action, userToken);
      toast(action === "approve" ? "PUBLISHED" : "Rejected",
            { description: `${r.ok} asset${r.ok !== 1 ? "s" : ""} ${action === "approve" ? "now live across the Archive" : "removed from the queue"}.` });
      setSelected((s) => { const n = new Set(s); ids.forEach((i) => n.delete(i)); return n; });
      setQueue((q) => (q || []).filter((x) => !ids.includes(x.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${action} failed`);
    } finally { setBusy(""); }
  };

  if (queue === null) return null;
  if (queue.length === 0 && !open) return null;

  return (
    <div className="border border-[#A68F5E]/40" data-testid="wiki-review-panel">
      <button onClick={() => setOpen((v) => !v)} data-testid="wiki-review-toggle"
              className="w-full flex items-center gap-2 px-3 min-h-[46px] text-left">
        <ShieldCheck size={13} className="text-[#A68F5E]" />
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E4DFD3]">
          Wikimedia Review Queue
        </span>
        <span className="font-mono text-[10px] text-[#A68F5E]" data-testid="wiki-review-count">
          {queue.length}
        </span>
        <span className="ml-auto font-mono text-[10px] text-[#757065]">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="border-t border-[#2D2A26] p-3 space-y-3">
          {queue.length === 0 ? (
            <div className="font-mono text-[11px] text-[#757065] italic py-4 text-center">
              Queue clear — every held match has been decided.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setTriage(true)}
                        data-testid="wiki-review-triage-btn"
                        className="inline-flex items-center gap-1.5 bg-[#D92D20] text-black px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.2em] hover:bg-[#b8261b]">
                  Rapid Triage
                </button>
                <button onClick={() => setSelected(new Set(queue.map((x) => x.id)))}
                        data-testid="wiki-review-select-all"
                        className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#9FA8D4] hover:underline">
                  Select all
                </button>
                <button onClick={() => setSelected(new Set())}
                        className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#757065] hover:underline">
                  Clear
                </button>
                {selected.size > 0 && (
                  <div className="ml-auto flex gap-2">
                    <button onClick={() => onDecide([...selected], "approve")} disabled={!!busy}
                            data-testid="wiki-review-bulk-approve"
                            className="inline-flex items-center gap-1.5 bg-[#1d3b2a] border border-[#3FB950]/50 text-[#5DD4B3] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50">
                      {busy === "approve" ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
                      Approve {selected.size}
                    </button>
                    <button onClick={() => onDecide([...selected], "reject")} disabled={!!busy}
                            data-testid="wiki-review-bulk-reject"
                            className="inline-flex items-center gap-1.5 border border-[#D92D20]/50 text-[#FFA9A0] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50">
                      <X size={11} /> Reject {selected.size}
                    </button>
                  </div>
                )}
              </div>
              <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
                {queue.slice(0, visible).map((item) => (
                  <QueueCard key={item.id} item={item}
                             checked={selected.has(item.id)}
                             onCheck={onCheck} onDecide={onDecide} busy={busy}
                             token={token} userToken={userToken} />
                ))}
                {queue.length > visible && (
                  <button onClick={() => setVisible((v) => v + 12)}
                          data-testid="wiki-review-load-more"
                          className="w-full border border-[#2D2A26] py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39E93] hover:border-[#A68F5E]/60">
                    Load {Math.min(12, queue.length - visible)} more · {queue.length - visible} remaining
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
      {triage && queue && queue.length > 0 && (
        <RapidTriage queue={queue} token={token} userToken={userToken}
                     onClose={() => { setTriage(false); load(); }} />
      )}
    </div>
  );
};

export default AdminWikiReviewQueue;
